import { Modal, Alert, Form } from 'react-bootstrap';
import Button from 'components/base/Button';
import { AssetRequest } from 'api/assetsAPI';
import { useState } from 'react';

// Generic request type that works with both AssetRequest and DepartmentRequest
interface BaseRequest {
  id: number;
  status: string;
  asset_name?: string;
  category_name?: string;
  request_note?: string;
  user_name?: string;
  priority?: string;
  created_at?: string;
}

interface DeleteAllRequestModalProps {
  show: boolean;
  handleClose: () => void;
  request: AssetRequest | BaseRequest;
  onCancel: (reason?: string) => Promise<void>;
  isAdmin?: boolean; // New prop to indicate if user is admin
}

const DeleteAllRequestModal = ({
  show,
  handleClose,
  request,
  onCancel,
  isAdmin = false
}: DeleteAllRequestModalProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancellationReason, setCancellationReason] = useState('');

  const handleCancel = async () => {
    setLoading(true);
    setError(null);

    // Validate reason if admin canceling approved request
    if (
      isAdmin &&
      request.status === 'approved' &&
      !cancellationReason.trim()
    ) {
      setError('Cancellation reason is required for approved requests');
      setLoading(false);
      return;
    }

    try {
      await onCancel(cancellationReason.trim() || undefined);
      handleClose();
      setCancellationReason('');
    } catch (err) {
      console.error('Failed to cancel request:', err);
      setError('Failed to cancel request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setError(null);
    setCancellationReason('');
    handleClose();
  };

  // Users can cancel pending, Admins can cancel pending or approved
  const canCancel = isAdmin
    ? request.status === 'pending' || request.status === 'approved'
    : request.status === 'pending';

  const requiresReason = isAdmin && request.status === 'approved';

  return (
    <Modal show={show} onHide={handleCloseModal} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {canCancel ? 'Cancel Request' : 'Request Status'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {canCancel ? (
          <>
            <Alert variant="warning" className="mb-3">
              Are you sure you want to cancel {isAdmin ? 'this' : 'your'}{' '}
              request?
            </Alert>

            <div className="p-3 border rounded bg-light mb-3">
              <div className="small">
                <div>
                  <strong>Asset:</strong>{' '}
                  {request.asset_name || 'Unknown Asset'}
                </div>
                <div>
                  <strong>Category:</strong>{' '}
                  {request.category_name || 'Unknown Category'}
                </div>
                <div>
                  <strong>Priority:</strong>
                  <span
                    className={`badge ms-1 ${
                      request.priority === 'high'
                        ? 'bg-danger'
                        : request.priority === 'medium'
                        ? 'bg-warning'
                        : 'bg-success'
                    }`}
                  >
                    {request.priority}
                  </span>
                </div>
                <div>
                  <strong>Status:</strong>
                  <span
                    className={`badge ms-1 ${
                      request.status === 'approved'
                        ? 'bg-success'
                        : 'bg-warning'
                    }`}
                  >
                    {request.status}
                  </span>
                </div>
                <div>
                  <strong>Request Note:</strong>{' '}
                  {request.request_note || 'No notes provided'}
                </div>
                {request.created_at && (
                  <div>
                    <strong>Requested on:</strong>{' '}
                    {new Date(request.created_at).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>

            {requiresReason && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Cancellation Reason <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={cancellationReason}
                  onChange={e => setCancellationReason(e.target.value)}
                  placeholder="Enter the reason for cancelling this approved request..."
                  required
                  disabled={loading}
                />
                <Form.Text className="text-muted">
                  This reason will be visible to the requester
                </Form.Text>
              </Form.Group>
            )}
          </>
        ) : (
          <div>
            <Alert variant="info" className="mb-3">
              This request cannot be cancelled because its status is{' '}
              <strong>{request.status}</strong>.
            </Alert>

            <div className="p-3 border rounded bg-light">
              <div className="small">
                <div>
                  <strong>Asset:</strong>{' '}
                  {request.asset_name || 'Unknown Asset'}
                </div>
                <div>
                  <strong>Requested by:</strong>{' '}
                  {request.user_name || 'Unknown User'}
                </div>
                <div>
                  <strong>Category:</strong>{' '}
                  {request.category_name || 'Unknown Category'}
                </div>
                <div>
                  <strong>Priority:</strong>
                  <span
                    className={`badge ms-1 ${
                      request.priority === 'high'
                        ? 'bg-danger'
                        : request.priority === 'medium'
                        ? 'bg-warning'
                        : 'bg-success'
                    }`}
                  >
                    {request.priority}
                  </span>
                </div>
                <div>
                  <strong>Status:</strong>
                  <span
                    className={`badge ms-1 ${
                      request.status === 'approved'
                        ? 'bg-success'
                        : request.status === 'rejected'
                        ? 'bg-danger'
                        : 'bg-secondary'
                    }`}
                  >
                    {request.status}
                  </span>
                </div>
                <div>
                  <strong>Request Note:</strong>{' '}
                  {request.request_note || 'No notes provided'}
                </div>
                {request.created_at && (
                  <div>
                    <strong>Requested on:</strong>{' '}
                    {new Date(request.created_at).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={handleCloseModal}
          disabled={loading}
        >
          {canCancel ? 'Keep Request' : 'Close'}
        </Button>
        {canCancel && (
          <Button variant="warning" onClick={handleCancel} disabled={loading}>
            {loading ? 'Cancelling...' : 'Yes, Cancel Request'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default DeleteAllRequestModal;
